// Archive textures must be small: JournalCanvas renders at export resolution (~3100px),
// so downscale to ≤ maxEdge before storing.

// Synchronous downscaled copy of the live page canvas (the snapshot that gets stored).
export function snapshotPreview(canvas: HTMLCanvasElement, maxEdge = 800): HTMLCanvasElement | null {
  const scale = Math.min(1, maxEdge / Math.max(canvas.width, canvas.height));
  const w = Math.max(1, Math.round(canvas.width * scale));
  const h = Math.max(1, Math.round(canvas.height * scale));
  const out = document.createElement('canvas');
  out.width = w; out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(canvas, 0, 0, w, h);
  return out;
}

export function encodePreview(out: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => out.toBlob((b) => resolve(b), 'image/jpeg', 0.78));
}

export function canvasToPreviewBlob(canvas: HTMLCanvasElement, maxEdge = 800): Promise<Blob | null> {
  const out = snapshotPreview(canvas, maxEdge);
  return out ? encodePreview(out) : Promise.resolve(null);
}

const INK_LUMA = 100; // a written page always has dark ink (the black date) in its top quarter
const MIN_ALPHA = 128; // transparent pixels (a cleared canvas) are not ink

// Pure RGBA scan: true when no opaque pixel darker than INK_LUMA exists in the first `rows`
// rows, sampled every `step` pixels in both axes.
export function pixelsMostlyBlank(data: Uint8ClampedArray, width: number, rows: number, step = 2): boolean {
  const h = Math.min(rows, Math.floor(data.length / 4 / Math.max(1, width)));
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < width; x += step) {
      const i = (y * width + x) * 4;
      if (data[i + 3] < MIN_ALPHA) continue;
      const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      if (luma < INK_LUMA) return false;
    }
  }
  return true;
}

// Samples the top 25% of a (downscaled) snapshot. Unreadable (no context / tainted) → false,
// so a capture we cannot inspect is never retried.
export function isMostlyBlank(canvas: HTMLCanvasElement): boolean {
  const rows = Math.max(1, Math.floor(canvas.height / 4));
  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    const img = ctx.getImageData(0, 0, canvas.width, rows);
    return pixelsMostlyBlank(img.data, canvas.width, rows);
  } catch { return false; }
}
